import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Button from '../common/Button';

interface CancellationReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, details: string) => void;
  loading?: boolean;
}

const PREDEFINED_REASONS = [
  "No response from the other person",
  "The project is no longer happening",
  "Found another collaborator",
  "Timeline mismatch",
  "Creative differences",
  "Personal reasons",
  "Other"
];

const CancellationReasonModal: React.FC<CancellationReasonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [details, setDetails] = useState<string>('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selectedReason) return;
    onConfirm(selectedReason, details);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10001]">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white dark:bg-gray-900 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800 max-h-[90vh] flex flex-col"
      >
        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center shrink-0">
          <div className="flex items-center space-x-2 text-rose-600 dark:text-rose-400">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <h3 className="text-lg font-bold">Cancel Collaboration</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
            <XMarkIcon className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-6 flex-1 overflow-y-auto">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            We are sorry to see this collaboration end. Please let us know the reason for cancellation to help us improve.
          </p>

          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Select a reason</p>
            <div className="grid grid-cols-1 gap-2">
              {PREDEFINED_REASONS.map((reason) => (
                <button
                  key={reason}
                  onClick={() => setSelectedReason(reason)}
                  className={`flex items-center p-3 rounded-xl border text-sm transition-all duration-200 ${
                    selectedReason === reason
                      ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800/50 text-rose-700 dark:text-rose-400 font-medium'
                      : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-200 dark:hover:border-gray-600'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border mr-3 flex items-center justify-center ${
                    selectedReason === reason ? 'border-rose-600 bg-rose-600' : 'border-gray-300'
                  }`}>
                    {selectedReason === reason && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  {reason}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Additional Details {selectedReason !== 'Other' && '(Optional)'}
            </p>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Tell us more about the reason..."
              className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl p-3 text-sm focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 outline-none transition-all dark:text-white h-24 resize-none"
              required={selectedReason === 'Other'}
            />
          </div>
        </div>

        <div className="p-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 flex space-x-3 shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 dark:border-gray-700 dark:text-gray-400"
            disabled={loading}
          >
            Go Back
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={loading}
            disabled={!selectedReason || (selectedReason === 'Other' && !details.trim())}
            className="flex-1 bg-rose-600 hover:bg-rose-700 dark:bg-rose-500 dark:hover:bg-rose-400"
          >
            Confirm Cancel
          </Button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default CancellationReasonModal;
